import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsArrowDown, BsDownload } from "react-icons/bs";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import ButtonImage from "../../../../components/UI/Button/ButtonImage";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import useApiClient, { apiRoutes } from "../../../../hooks/apiClient";
import DateTime from "../../../../components/Formatters/DateTime";
import useAndamentoProrrogacaoOs from "../hooks/useAndamentoProrrogacaoOs";
import constants from "../../../Constants";
import useDocumentoProrrogacao from "../hooks/useDocumentoProrrogacao";

const AndamentoProrrogacaoOs = ({ numOs, numProrrogacaoOs }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const setSelection = useState({})[1];

  const { setDocParams } = useAndamentoProrrogacaoOs();
  const { request: requestAndamento, data, errors: error, isLoading } = useApiClient();
  const { buscaDocumento } = useDocumentoProrrogacao();

  // Mantém função disponível para outros componentes chamarem (se necessário)
  const andamentoProrrogacaoOs = useMemo(
    () => ({
      exibir: (row) => {
        setDocParams({
          sqPedidoProrrogacao: numProrrogacaoOs,
          sqAndamento: row.sqAndamento,
        });
      },
    }),
    [numProrrogacaoOs, setDocParams],
  );

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (numProrrogacaoOs) {
      const values = {
        PedidoProrrogacao: numProrrogacaoOs,
        pageSize,
        pageNumber,
      };
      requestAndamento(apiRoutes.fiscalizacao.prorrogacaoos.andamentoProrrogacaoOs, values);
    }
  }, [requestAndamento, numProrrogacaoOs, pageSize, pageNumber]);

  useEffect(() => {
    if (data && data.result) {
      if (data.result.length && !data.result[0].totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados.`);
        return;
      }

      const preRows = data.result.map((item) => ({
        ...item,
        disabled: !(item.anexos?.length > 0),
      }));

      setRows(preRows || []);
      setTotalRows(data.result.length ? data.result[0].totalRows : 0);
    }

    if (error) {
      toast.error(error.message);
    }
  }, [data, error]);

  // ✅ Define colunas dentro do componente (assim pode usar buscaDocumento)
  const columns = useMemo(
    () => [
      {
        name: "Andamento",
        selector: "andamento",
        maxWidth: "19%",
        center: true,
        sortable: true,
        cell: (row) => <DateTime value={row.andamento} stringFormat="dd/MM/yyyy HH:mm:ss" />,
      },
      {
        name: "Atividade",
        selector: "atividade",
        maxWidth: "35%",
        sortable: true,
        cell: (row) => (
          <OverlayTrigger placement="top" overlay={<Tooltip>{row.atividade}</Tooltip>} data-tag="allowRowEvents">
            <Button
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#858796",
                textDecoration: "none",
              }}
              variant="link"
              data-tag="allowRowEvents"
            >
              {row.atividade}
            </Button>
          </OverlayTrigger>
        ),
      },
      {
        name: "Tempo Utilizado",
        selector: "tempoUtilizado",
        center: true,
        maxWidth: "20%",
      },
      {
        name: "Usuário",
        selector: "usuario",
        maxWidth: "25%",
      },
      {
        name: "Ações",
        center: true,
        maxWidth: "1%",
        cell: (row) => (
          <ButtonImage
            variant="btn-link"
            className="btn-lg pa-0 p-0 mx-1"
            buttonImage={<BsDownload size="15" style={{ color: "#E65729" }} />}
            tooltipImage="Visualizar Documento"
            onClick={() => buscaDocumento(numOs, numProrrogacaoOs, row.sqAndamento)}
          />
        ),
      },
    ],
    [buscaDocumento, numProrrogacaoOs, numOs],
  );

  return (
    <div style={{ width: "100%" }}>
      <DataTable
        noHeader
        keyField="sqAndamento"
        columns={columns}
        data={rows}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        sortIcon={<BsArrowDown />}
        sortable
        paginationTotalRows={totalRows}
        selectableRows={false}
        onSelectedRowsChange={(state) => setSelection(state.selectedRows)}
        pointerOnHover
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationPerPage={pageSize}
      />
    </div>
  );
};

export default AndamentoProrrogacaoOs;
